import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  Button,
} from '@material-ui/core';
import { useBillingStyles, getBackendUrl, getAuthHeaders, BRAND_COLORS } from '../styles/billingStyles';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import useClinicianAuthGuard from './useClinicianAuthGuard';

/**
 * BillingDashboard - Main billing system component
 * Integrates invoice management, metrics, and patient billing
 * 
 * Features:
 * - Tab-based interface (Metrics | Unbilled | Invoices | Aging Report | Settings)
 * - Real-time data from backend API
 * - Role-based access (staff/admin only)
 */
const BillingDashboard = () => {
  const classes = useBillingStyles();
  const { isAuthorizedClinician } = useClinicianAuthGuard();
  const token = useSelector((state) => state.auth.token);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const backendUrl = getBackendUrl();

  // UI state
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dashboard data
  const [metricsData, setMetricsData] = useState(null);
  const [unbilledAppointments, setUnbilledAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [agingReport, setAgingReport] = useState(null);

  useEffect(() => {
    if (isLoggedIn && token && currentTab === 0) {
      fetchMetrics();
    }
  }, [isLoggedIn, token, currentTab]);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/calendar/billing-metrics/`, {
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch metrics: ${response.status}`);
      }

      const data = await response.json();
      setMetricsData(data);
    } catch (err) {
      console.error('Metrics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnbilledAppointments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${backendUrl}/api/calendar/appointments/?billing_status=unbilled`,
        { headers: getAuthHeaders(token) }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch appointments: ${response.status}`);
      }

      const data = await response.json();
      setUnbilledAppointments(data.results || data);
    } catch (err) {
      console.error('Unbilled appointments fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/calendar/invoices/`, {
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoices: ${response.status}`);
      }

      const data = await response.json();
      setInvoices(data.results || data);
    } catch (err) {
      console.error('Invoices fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgingReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/calendar/invoices/ageing-report/`, {
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch aging report: ${response.status}`);
      }

      const data = await response.json();
      setAgingReport(data);
    } catch (err) {
      console.error('Aging report fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setError(null);

    // Load tab-specific data
    switch (newValue) {
      case 0:
        fetchMetrics();
        break;
      case 1:
        fetchUnbilledAppointments();
        break;
      case 2:
        fetchInvoices();
        break;
      case 3:
        fetchAgingReport();
        break;
      default:
        break;
    }
  };

  if (!isAuthorizedClinician || !isLoggedIn || !token) {
    return (
      <Box p={3}>
        <Typography color="error">Redirecting...</Typography>
      </Box>
    );
  }

  return (
    <Box className="admin-dash">
      {/* Header */}
      <Card className={classes.sectionCard} style={{ marginTop: 0 }}>
        <CardHeader
          className={classes.cardHeader}
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <AssessmentIcon style={{ color: BRAND_COLORS.navy }} />
              <Typography variant="h6" style={StyleSheet.titleStyle}>
                Billing Management
              </Typography>
            </Box>
          }
          subheader="Invoice creation, tracking, and revenue metrics"
        />
      </Card>

      {/* Error Alert */}
      {error && (
        <Card style={{ marginTop: 16, marginBottom: 16, backgroundColor: '#ffebee', borderLeft: `4px solid ${BRAND_COLORS.error}` }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography color="error" style={{ fontWeight: 600 }}>
                {error}
              </Typography>
              <Button
                size="small"
                onClick={() => handleTabChange(null, currentTab)}
                style={{ color: BRAND_COLORS.error, textTransform: 'uppercase' }}
              >
                Retry
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tab Navigation */}
      <Card className={classes.sectionCard}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          style={{ borderBottom: `2px solid ${BRAND_COLORS.orange}` }}
        >
          <Tab
            icon={<AssessmentIcon />}
            label="Metrics"
            style={{ fontWeight: 600, color: BRAND_COLORS.navy }}
          />
          <Tab
            icon={<MoneyOffIcon />}
            label="Unbilled"
            style={{ fontWeight: 600, color: BRAND_COLORS.navy }}
          />
          <Tab
            icon={<ReceiptIcon />}
            label="Invoices"
            style={{ fontWeight: 600, color: BRAND_COLORS.navy }}
          />
          <Tab
            icon={<TrendingUpIcon />}
            label="Aging Report"
            style={{ fontWeight: 600, color: BRAND_COLORS.navy }}
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      <Box style={{ marginTop: 16 }}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={60} style={{ color: BRAND_COLORS.navy }} />
          </Box>
        ) : currentTab === 0 ? (
          <MetricsTab data={metricsData} classes={classes} />
        ) : currentTab === 1 ? (
          <UnbilledTab appointments={unbilledAppointments} classes={classes} />
        ) : currentTab === 2 ? (
          <InvoicesTab invoices={invoices} classes={classes} />
        ) : currentTab === 3 ? (
          <AgingReportTab data={agingReport} classes={classes} />
        ) : null}
      </Box>
    </Box>
  );
};

/**
 * MetricsTab - Shows billing metrics
 */
const MetricsTab = ({ data, classes }) => {
  if (!data) {
    return (
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography color="textSecondary" align="center">
            No metrics data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statCard}>
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              Total Revenue
            </Typography>
            <Typography variant="h5" style={{ color: BRAND_COLORS.navy, fontWeight: 600 }}>
              {data.total_revenue ? `DKK ${data.total_revenue.toFixed(2)}` : '—'}
            </Typography>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statCard}>
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              Total Invoices
            </Typography>
            <Typography variant="h5" style={{ color: BRAND_COLORS.navy, fontWeight: 600 }}>
              {data.total_invoices || 0}
            </Typography>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statCard}>
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              Outstanding
            </Typography>
            <Typography variant="h5" style={{ color: BRAND_COLORS.warning, fontWeight: 600 }}>
              {data.outstanding_amount ? `DKK ${data.outstanding_amount.toFixed(2)}` : '—'}
            </Typography>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statCard}>
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              Collection Rate
            </Typography>
            <Typography variant="h5" style={{ color: BRAND_COLORS.success, fontWeight: 600 }}>
              {data.collection_rate ? `${data.collection_rate.toFixed(1)}%` : '—'}
            </Typography>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

/**
 * UnbilledTab - Shows appointments ready to be billed
 */
const UnbilledTab = ({ appointments, classes }) => {
  if (appointments.length === 0) {
    return (
      <Card className={classes.sectionCard}>
        <CardContent align="center">
          <Typography color="textSecondary">No unbilled appointments</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.sectionCard}>
      <CardContent>
        <Typography variant="h6" className={classes.cardTitle} style={{ marginBottom: 16 }}>
          {appointments.length} Unbilled Appointments
        </Typography>
        {/* TODO: Render appointment list here */}
        <Typography color="textSecondary">Component placeholder - table coming in next phase</Typography>
      </CardContent>
    </Card>
  );
};

/**
 * InvoicesTab - Shows created invoices
 */
const InvoicesTab = ({ invoices, classes }) => {
  if (invoices.length === 0) {
    return (
      <Card className={classes.sectionCard}>
        <CardContent align="center">
          <Typography color="textSecondary">No invoices created</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.sectionCard}>
      <CardContent>
        <Typography variant="h6" className={classes.cardTitle} style={{ marginBottom: 16 }}>
          {invoices.length} Invoices
        </Typography>
        {/* TODO: Render invoice list here */}
        <Typography color="textSecondary">Component placeholder - table coming in next phase</Typography>
      </CardContent>
    </Card>
  );
};

/**
 * AgingReportTab - Shows invoice aging analysis
 */
const AgingReportTab = ({ data, classes }) => {
  if (!data) {
    return (
      <Card className={classes.sectionCard}>
        <CardContent align="center">
          <Typography color="textSecondary">No aging data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.sectionCard}>
      <CardContent>
        <Typography variant="h6" className={classes.cardTitle} style={{ marginBottom: 16 }}>
          Invoice Aging Analysis
        </Typography>
        {/* TODO: Render aging analysis here */}
        <Typography color="textSecondary">Component placeholder - chart coming in next phase</Typography>
      </CardContent>
    </Card>
  );
};

const StyleSheet = {
  titleStyle: {
    color: BRAND_COLORS.navy,
    fontWeight: 600,
  },
};

export default BillingDashboard;
